import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import saveAs from 'file-saver';
import { CompaniesContractDialogComponent } from 'src/app/companies/companies-contract-dialog/companies-contract-dialog.component';
import { Company } from 'src/app/core/companies.service';
import {
  CompanyContract,
  CompanyContractsService,
  CompanyContractStatus,
} from 'src/app/core/company-contracts.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { FormErrorStateMatcher, FormService } from 'src/app/core/form.service';
import { UtilsService } from 'src/app/core/utils.service';

@Component({
  selector: 'app-company-details-general-contracts',
  templateUrl: './company-details-general-contracts.component.html',
  styleUrls: ['./company-details-general-contracts.component.scss'],
})
export class CompanyDetailsGeneralContractsComponent {
  @Input() contracts?: CompanyContract[];
  @Input() company?: Company;
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();

  create_contract_from_group: FormGroup = this.form_service.create_contract_from_group;
  error_matcher: FormErrorStateMatcher = this.form_service.matcher;
  is_list_running: boolean = false;
  is_generate_running: boolean = false;
  is_contract_download_loading: boolean = false;

  constructor(
    private company_contracts_service: CompanyContractsService,
    private error_service: ErrorService,
    private utils_service: UtilsService,
    private form_service: FormService,
    private translate: TranslateService,
  ) {}

  onContractClick(contract: CompanyContract): void {
    if (contract.signed_file_url) {
      this.downloadContract(contract, true);
    }

    if (contract.file_url && !contract.signed_file_url) {
      this.downloadContract(contract, false);
    }
  }

  onNewContractClick(): void {
    if (this.isFormValid() && this.company?.company_id) {
      const sales_network_id: string = this.create_contract_from_group.get(
        this.form_service.sales_network_id_label,
      )?.value;
      this.createContract(this.company.company_id, sales_network_id);
    }
  }

  onAcceptContractClick(contract: CompanyContract): void {
    if (this.company?.company_id && contract.company_contract_id) {
      this.acceptContract(this.company.company_id, contract.company_contract_id);
    }
  }

  onDenyContractClick(contract: CompanyContract): void {
    if (this.company?.company_id && contract.company_contract_id) {
      this.denyContract(this.company.company_id, contract.company_contract_id);
    }
  }

  onDeleteClick(contract: CompanyContract): void {
    if (this.company?.company_id && contract.company_contract_id) {
      this.deleteContract(this.company.company_id, contract.company_contract_id);
    }
  }

  downloadContract(contract: CompanyContract, is_signed: boolean, filenamePrefix?: string): void {
    this.is_contract_download_loading = true;
    const contract_prefix = this.translate.instant('COMPANIES.CONTRACT_PREFIX');
    const filename =
      contract_prefix +
      '_' +
      this.company?.sales_network_id +
      (is_signed ? '_' + this.translate.instant('COMPANIES.SIGNED') : '');
    this.company_contracts_service
      .download(contract?.company_contract_id as string, contract?.company_id as string, is_signed)
      .subscribe({
        next: response => {
          saveAs(response.data, filename);
          this.is_contract_download_loading = false;
        },
        error: error => {
          this.error_service.processGenericApiError(error);
          this.is_contract_download_loading = false;
        },
      });
  }

  createContract(company_id: string, sales_network_id: string): void {
    this.is_generate_running = true;
    this.company_contracts_service.createContract(company_id, sales_network_id).subscribe({
      next: () => {
        this.refresh.emit();
        this.is_generate_running = false;
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.refresh.emit();
        this.is_generate_running = false;
      },
    });
  }

  acceptContract(company_id: string, contract_id: string): void {
    this.is_list_running = true;
    this.company_contracts_service.acceptContract(company_id, contract_id).subscribe({
      next: () => {
        this.refresh.emit();
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.refresh.emit();
        this.is_list_running = false;
      },
    });
  }

  denyContract(company_id: string, contract_id: string): void {
    this.is_list_running = true;
    this.company_contracts_service.denyContract(company_id, contract_id).subscribe({
      next: () => {
        this.refresh.emit();
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.refresh.emit();
        this.is_list_running = false;
      },
    });
  }

  deleteContract(company_id: string, contract_id: string): void {
    this.is_list_running = true;
    this.company_contracts_service.deleteContract(company_id, contract_id).subscribe({
      next: () => {
        console.log('sono in ok');
        this.refresh.emit();
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        console.log('sono in ko');
        this.refresh.emit();
        this.is_list_running = false;
      },
    });
  }

  getContractStatusBackgroundClass(contract: CompanyContract): { [key: string]: boolean } {
    return this.utils_service.getContractStatusBackgroundClass(contract);
  }

  getContractStatusColorClass(contract: CompanyContract): { [key: string]: boolean } {
    return this.utils_service.getContractStatusColorClass(contract);
  }

  isContractSigned(contract: CompanyContract): boolean {
    return contract.status == CompanyContractStatus.signed;
  }

  isContractAccepted(contract: CompanyContract): boolean {
    return contract.status == CompanyContractStatus.accepted;
  }

  isFormValid(): boolean {
    return this.form_service.validateForm(this.create_contract_from_group);
  }
}
